import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, Send, AlertCircle, CheckCircle, MessageSquare, Clock, ChevronDown } from 'lucide-react';
import { useTranslation } from '../i18n/LanguageContext';

function useInView(threshold = 0.1) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function Reveal({ children, delay = 0 }) {
  const [ref, inView] = useInView(0.1);
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? 'none' : 'translateY(32px)',
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s cubic-bezier(0.16,1,0.3,1) ${delay}s`,
    }}>
      {children}
    </div>
  );
}

function FaqItem({ question, answer, cardBg, cardBorder, textPrimary, textSecondary }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s' }}>
      <button onClick={() => setOpen(!open)} style={{
        width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '16px 20px', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left', gap: 12,
      }}>
        <span style={{ fontWeight: 700, fontSize: 14, color: textPrimary }}>{question}</span>
        <ChevronDown size={16} color={textSecondary} style={{ flexShrink: 0, transition: 'transform 0.3s', transform: open ? 'rotate(180deg)' : 'none' }} />
      </button>
      {open && (
        <div style={{ padding: '0 20px 16px', color: textSecondary, fontSize: 13, lineHeight: 1.65 }}>
          {answer}
        </div>
      )}
    </div>
  );
}

export default function DerrDigitalKontaktseite({ darkMode = false }) {
  const { t } = useTranslation();
  const [formStatus, setFormStatus] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [focused, setFocused] = useState(null);
  const [formData, setFormData] = useState({
    name: '', email: '', telefon: '', betreff: 'allgemeine-anfrage', nachricht: '', datenschutz: false
  });

  const bg           = darkMode ? '#0a0a0f' : '#f8faff';
  const textPrimary  = darkMode ? '#ffffff' : '#0f0f1a';
  const textSecondary= darkMode ? 'rgba(255,255,255,0.6)' : 'rgba(15,15,26,0.55)';
  const cardBg       = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.9)';
  const cardBorder   = darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';
  const inputBg      = darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)';
  const accent       = '#6366f1';
  const accent2      = '#8b5cf6';

  useEffect(() => {
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
    return () => { document.documentElement.style.background = ''; document.body.style.background = ''; };
  }, [bg]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.nachricht || !formData.datenschutz) {
      setFormStatus('error');
      setTimeout(() => setFormStatus(null), 5000);
      return;
    }
    try {
      setIsSubmitting(true);
      const response = await fetch('http://localhost:5000/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      const result = await response.json();
      if (response.ok && result.success) {
        setFormStatus('success');
        setFormData({ name: '', email: '', telefon: '', betreff: 'allgemeine-anfrage', nachricht: '', datenschutz: false });
      } else {
        setFormStatus('error');
      }
    } catch {
      setFormStatus('error');
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setFormStatus(null), 5000);
    }
  };

  const inputStyle = (field) => ({
    width: '100%', padding: '11px 14px', borderRadius: 10, fontSize: 14,
    background: inputBg, color: textPrimary, outline: 'none',
    border: `1px solid ${focused === field ? accent : cardBorder}`,
    transition: 'border-color 0.2s',
    boxShadow: focused === field ? `0 0 0 3px ${accent}20` : 'none',
    boxSizing: 'border-box',
  });

  const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: textSecondary, marginBottom: 6, letterSpacing: '0.03em' };

  const faqs = [
    { q: t('contact.faq1Question'), a: t('contact.faq1Answer') },
    { q: t('contact.faq2Question'), a: t('contact.faq2Answer') },
    { q: t('contact.faq3Question'), a: t('contact.faq3Answer') },
    { q: t('contact.faq4Question'), a: t('contact.faq4Answer') },
  ];

  return (
    <div style={{ background: bg, color: textPrimary, minHeight: '100vh' }}>
      <style>{`
        ::placeholder { color: ${textSecondary}; opacity: 1; }
        select option { background: ${darkMode ? '#1a1a2e' : '#fff'}; color: ${textPrimary}; }
        input[type="checkbox"] { accent-color: ${accent}; }
      `}</style>

      {/* ── HERO ── */}
      <section style={{
        position: 'relative', minHeight: '52vh', display: 'flex', alignItems: 'center',
        paddingTop: 'clamp(100px, 14vh, 140px)', paddingBottom: 60, overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, background: darkMode
          ? 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(99,102,241,0.18) 0%, transparent 65%)'
          : 'radial-gradient(ellipse 70% 60% at 50% 0%, rgba(99,102,241,0.09) 0%, transparent 65%)' }} />

        <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative', width: '100%' }}>
          <Reveal>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 20,
              padding: '6px 16px', borderRadius: 999, fontSize: 13, color: accent2,
              background: darkMode ? 'rgba(99,102,241,0.1)' : 'rgba(99,102,241,0.08)',
              border: `1px solid ${darkMode ? 'rgba(99,102,241,0.35)' : 'rgba(99,102,241,0.25)'}`,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: accent, display: 'inline-block', boxShadow: `0 0 0 3px ${accent}30` }} />
              Antwort innerhalb von 24h
            </div>
            <h1 style={{ fontSize: 'clamp(32px, 5vw, 72px)', fontWeight: 900, lineHeight: 1.05, letterSpacing: '-0.03em', marginBottom: 16 }}>
              Lass uns dein Projekt<br />
              <span style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                gemeinsam starten.
              </span>
            </h1>
            <p style={{ fontSize: 'clamp(14px, 1.5vw, 18px)', color: textSecondary, lineHeight: 1.65, marginBottom: 32, maxWidth: 520, margin: '0 auto 32px' }}>
              {t('contact.heroSubtitle1')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="tel:+491741628004" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', borderRadius: 12,
                background: `linear-gradient(135deg, ${accent}, ${accent2})`, color: '#fff',
                fontWeight: 700, fontSize: 14, textDecoration: 'none',
                boxShadow: `0 8px 24px ${accent}30`, transition: 'transform 0.2s, box-shadow 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.04)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                <Phone size={15} /> {t('contact.callDirect')}
              </a>
              <a href="mailto:info@derrdigital.de" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', borderRadius: 12,
                border: `1.5px solid ${cardBorder}`, color: textPrimary,
                fontWeight: 600, fontSize: 14, textDecoration: 'none',
                background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)',
                transition: 'border-color 0.2s, background 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = accent; e.currentTarget.style.background = `${accent}10`; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = cardBorder; e.currentTarget.style.background = darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'; }}
              >
                <Mail size={15} /> {t('contact.sendEmail')}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── CONTACT INFO + FORM ── */}
      <section style={{ padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ maxWidth: 1280, margin: '0 auto', padding: '0 24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 32, alignItems: 'start' }}>

            {/* Left: contact info */}
            <div>
              <Reveal>
                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 12 }}>Kontakt</div>
                <h2 style={{ fontSize: 'clamp(22px, 3vw, 40px)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em', marginBottom: 12 }}>
                  {t('contact.infoTitle')}
                </h2>
                <p style={{ color: textSecondary, fontSize: 14, lineHeight: 1.65, marginBottom: 28 }}>
                  {t('contact.infoDescription')}
                </p>
              </Reveal>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 32 }}>
                {[
                  { Icon: Mail,  color: accent,  label: 'E-Mail', value: 'info@derrdigital.de', href: 'mailto:info@derrdigital.de', delay: 0.05 },
                  { Icon: Phone, color: '#8b5cf6', label: 'Telefon', value: '+49 174 1628004', href: 'tel:+491741628004', delay: 0.1 },
                  { Icon: Clock, color: '#a855f7', label: 'Erreichbarkeit', value: 'Mo–Fr, 9–18 Uhr', href: null, delay: 0.15 },
                  { Icon: MessageSquare, color: '#ec4899', label: 'Reaktionszeit', value: 'Innerhalb von 24h', href: null, delay: 0.2 },
                ].map(({ Icon, color, label, value, href, delay }) => (
                  <Reveal key={label} delay={delay}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 14, padding: '14px 16px', borderRadius: 12,
                      background: cardBg, border: `1px solid ${cardBorder}`, transition: 'border-color 0.2s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.borderColor = color + '50'}
                      onMouseLeave={e => e.currentTarget.style.borderColor = cardBorder}
                    >
                      <div style={{ width: 36, height: 36, borderRadius: 9, background: color + '18', color, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon size={16} strokeWidth={1.8} />
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: textSecondary, marginBottom: 2 }}>{label}</div>
                        {href
                          ? <a href={href} style={{ fontSize: 14, fontWeight: 600, color: textPrimary, textDecoration: 'none' }}
                              onMouseEnter={e => e.currentTarget.style.color = color}
                              onMouseLeave={e => e.currentTarget.style.color = textPrimary}>{value}</a>
                          : <div style={{ fontSize: 14, fontWeight: 600, color: textPrimary }}>{value}</div>
                        }
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            </div>

            {/* Right: form */}
            <Reveal delay={0.1}>
              <div style={{ background: cardBg, border: `1px solid ${cardBorder}`, borderRadius: 18, padding: '28px 24px', position: 'relative', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 2, background: `linear-gradient(90deg, transparent, ${accent}, ${accent2}, transparent)` }} />

                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 6 }}>Schreib uns</div>
                <h3 style={{ fontSize: 20, fontWeight: 800, marginBottom: 20, color: textPrimary }}>{t('contact.formTitle2')}</h3>

                {formStatus === 'success' && (
                  <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 10, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', display: 'flex', gap: 10, color: '#22c55e' }}>
                    <CheckCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{t('contact.successTitle')}</div>
                      <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{t('contact.successMessage')}</div>
                    </div>
                  </div>
                )}
                {formStatus === 'error' && (
                  <div style={{ marginBottom: 20, padding: '12px 16px', borderRadius: 10, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', display: 'flex', gap: 10, color: '#ef4444' }}>
                    <AlertCircle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 13 }}>{t('contact.errorTitle')}</div>
                      <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{t('contact.errorMessage')}</div>
                    </div>
                  </div>
                )}

                <form onSubmit={handleSubmit}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={labelStyle}>{t('contact.labelName')} <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="text" name="name" value={formData.name} onChange={handleInputChange}
                        style={inputStyle('name')} placeholder={t('contact.placeholderName')}
                        onFocus={() => setFocused('name')} onBlur={() => setFocused(null)} required />
                    </div>
                    <div>
                      <label style={labelStyle}>{t('contact.labelEmail')} <span style={{ color: '#ef4444' }}>*</span></label>
                      <input type="email" name="email" value={formData.email} onChange={handleInputChange}
                        style={inputStyle('email')} placeholder={t('contact.placeholderEmail')}
                        onFocus={() => setFocused('email')} onBlur={() => setFocused(null)} required />
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                    <div>
                      <label style={labelStyle}>{t('contact.labelPhone')}</label>
                      <input type="tel" name="telefon" value={formData.telefon} onChange={handleInputChange}
                        style={inputStyle('telefon')} placeholder="+49 123 456789"
                        onFocus={() => setFocused('telefon')} onBlur={() => setFocused(null)} />
                    </div>
                    <div>
                      <label style={labelStyle}>{t('contact.labelSubject')} <span style={{ color: '#ef4444' }}>*</span></label>
                      <select name="betreff" value={formData.betreff} onChange={handleInputChange}
                        style={{ ...inputStyle('betreff'), appearance: 'none' }}
                        onFocus={() => setFocused('betreff')} onBlur={() => setFocused(null)} required>
                        <option value="allgemeine-anfrage">{t('contact.subjectGeneral')}</option>
                        <option value="beratungsgespräch">{t('contact.subjectConsult')}</option>
                        <option value="website-projekt">{t('contact.subjectWebsite')}</option>
                        <option value="software-entwicklung">{t('contact.subjectSoftware')}</option>
                        <option value="cps-system">{t('contact.subjectCps')}</option>
                        <option value="support">{t('contact.subjectSupport')}</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label style={labelStyle}>{t('contact.labelMessage')} <span style={{ color: '#ef4444' }}>*</span></label>
                    <textarea name="nachricht" value={formData.nachricht} onChange={handleInputChange} rows={5}
                      style={{ ...inputStyle('nachricht'), resize: 'none' }}
                      placeholder={t('contact.placeholderMessage')}
                      onFocus={() => setFocused('nachricht')} onBlur={() => setFocused(null)} required />
                  </div>

                  <div style={{ marginBottom: 20 }}>
                    <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                      <input type="checkbox" name="datenschutz" checked={formData.datenschutz} onChange={handleInputChange}
                        style={{ marginTop: 2, flexShrink: 0, width: 15, height: 15 }} required />
                      <span style={{ fontSize: 12, color: textSecondary, lineHeight: 1.5 }}>
                        {t('contact.privacyConsentPrefix')}{' '}
                        <Link to="/datenschutz" style={{ color: accent, textDecoration: 'none' }}>{t('contact.privacyLink')}</Link>
                        {' '}{t('contact.privacyConsentSuffix')} <span style={{ color: '#ef4444' }}>*</span>
                      </span>
                    </label>
                  </div>

                  <button type="submit" disabled={isSubmitting} style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    padding: '13px 24px', borderRadius: 12, border: 'none', cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    background: `linear-gradient(135deg, ${accent}, ${accent2})`, color: '#fff',
                    fontWeight: 700, fontSize: 14, opacity: isSubmitting ? 0.7 : 1,
                    boxShadow: `0 8px 24px ${accent}30`, transition: 'transform 0.2s, opacity 0.2s',
                  }}
                    onMouseEnter={e => { if (!isSubmitting) e.currentTarget.style.transform = 'scale(1.02)'; }}
                    onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
                  >
                    {isSubmitting ? (
                      <><div style={{ width: 16, height: 16, border: '2px solid rgba(255,255,255,0.3)', borderTop: '2px solid #fff', borderRadius: '50%', animation: 'spin-contact 0.8s linear infinite' }} />{t('contact.sending')}</>
                    ) : (
                      <><Send size={15} />{t('contact.sendMessage')}</>
                    )}
                  </button>
                </form>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '80px 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: darkMode ? 'rgba(99,102,241,0.03)' : 'rgba(99,102,241,0.02)', borderTop: `1px solid ${cardBorder}`, borderBottom: `1px solid ${cardBorder}` }} />
        <div style={{ maxWidth: 760, margin: '0 auto', padding: '0 24px', position: 'relative' }}>
          <Reveal>
            <div style={{ textAlign: 'center', marginBottom: 36 }}>
              <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.1em', color: accent2, textTransform: 'uppercase', marginBottom: 10 }}>FAQ</div>
              <h2 style={{ fontSize: 'clamp(22px, 3vw, 40px)', fontWeight: 900, lineHeight: 1.1, letterSpacing: '-0.02em' }}>
                {t('contact.faqTitle2')}
              </h2>
            </div>
          </Reveal>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {faqs.map((faq, i) => (
              <Reveal key={i} delay={i * 0.05}>
                <FaqItem question={faq.q} answer={faq.a} cardBg={cardBg} cardBorder={cardBorder} textPrimary={textPrimary} textSecondary={textSecondary} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{ padding: 'clamp(40px, 6vh, 64px) 0', position: 'relative', overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.25)' }} />
        <div style={{ maxWidth: 640, margin: '0 auto', padding: '0 24px', textAlign: 'center', position: 'relative' }}>
          <Reveal>
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 36px)', fontWeight: 900, color: '#fff', lineHeight: 1.1, marginBottom: 10 }}>
              {t('contact.ctaTitle')}
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 14, lineHeight: 1.6, marginBottom: 24 }}>
              {t('contact.ctaDescription')}
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="tel:+491741628004" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', borderRadius: 12,
                background: '#fff', color: accent, fontWeight: 700, fontSize: 14, textDecoration: 'none',
                boxShadow: '0 8px 24px rgba(0,0,0,0.2)', transition: 'transform 0.2s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.04)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
              >
                <Phone size={14} /> {t('contact.ctaCall')}
              </a>
              <a href="mailto:info@derrdigital.de" style={{
                display: 'inline-flex', alignItems: 'center', gap: 8, padding: '12px 28px', borderRadius: 12,
                border: '1.5px solid rgba(255,255,255,0.4)', color: '#fff', fontWeight: 600, fontSize: 14, textDecoration: 'none',
                background: 'rgba(255,255,255,0.1)', transition: 'background 0.2s, border-color 0.2s',
              }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.2)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.7)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.4)'; }}
              >
                <Mail size={14} /> {t('contact.ctaSendEmail')}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <style>{`@keyframes spin-contact { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
